/*global SunergoPlatform, turnstile*/

import axios from 'axios';

/**
 * Remotely submit a form, remotely validating along the way.
 * @param {object} form - the form item.
 * @returns early if no remote submission for this form or if there is an error.
 */
const initRemoteFormSubmit = function(form) {
	if (!form.element.dataset.remoteSubmit) return false;

	// Get the URLs that will be used to remotely submit and redirect post-submission.
	const remoteURL = form.element.action;
	const redirectURL = form.element.dataset.redirect;
	const defaultTimeout = 5000;

	// Setup the function to call when ready to remotely submit.
	form.remoteSubmit = function() {
		// Clear any previous error messages.
		form.fieldFeedback.clear();
		
		// Prepare form for submission.
		let data = new FormData(form.element);
		if (form.element.enctype == 'application/x-www-form-urlencoded') {
			// Regular form needs to be encoded differently.
			let params = new URLSearchParams();
			for (var [key, value] of data.entries()) params.append(key, value);
			data = params;
		}

		const httpHeaders = {
			'Content-Type': form.element.enctype
		};

		const csrfToken = form.element.querySelector('[name="_csrf"]');
		if (csrfToken) httpHeaders['csrf-token'] = csrfToken.value;

		// Submit the form remotely.
		axios({
			method: 'post',
			url: remoteURL,
			data: data,
			timeout: parseInt(form.element.dataset.axiosTimeout) || defaultTimeout,
			headers: httpHeaders
		})
			.then(res => {
				// If the data received is missing a status, it is malformed and can't be handled in this process.
				if (!res.data || !res.data.status)
					throw {
						debug: 'Malformed response data from remote submit',
						res
					};

				// Check if form has been successfully validated remotely.
				if (res.data.status === 'success') {
					// Check if the form is supposed to POST this again after remote submission.
					// If not we'll just redirect the URL.
					if (!form.element.dataset.redirectSubmit) {
						window.location = redirectURL;
						return;
					}
					form.element.action = redirectURL;
					form.element.submit();
					return;
				}

				if (res.data.status === 'session-expired') {
					if (!(res.data.msg || res.data.title || res.data.loginUrl))
						throw {
							debug: 'Session expired message incomplete',
							res
						};

					SunergoPlatform.feedbackBanner.show({
						id: `${form.name}_form-error`,
						type: 'warning',
						title: res.data.msg && res.data.msg.title ? res.data.msg.title : '',
						content: res.data.msg
							? res.data.msg.content ? res.data.msg.content : res.data.msg
							: 'You were logged out due to inactivity. Please log in in order to submit the form.',
						actions: {
							label: res.data.loginLabel || 'Log in (opens in new window)',
							action: 'open-window',
							attributes: {
								'data-href': res.data.loginURL
							}
						}
					});
					form.activeErrors = true;
					form.processing.set(false);
					return;
				}

				// Handle a redirect during form submit.
				if (res.data.redirect) return window.location.href = res.data.redirect;

				// If there are no error fields in the response, this is a non-standard error and we'll need to handle it differently.
				if (!res.data.errorFields || Object.entries(res.data.errorFields).length < 1)
					throw { status: res.data.status, response: res };

				// Show errors in in-field feedback.
				for (const field in res.data.errorFields) form.fieldFeedback.set(field, 'error', res.data.errorFields[field]);

				// Let form know there are errors and stop processing.
				if (!res.data.noGlobalError) form.fieldFeedback.checkErrors();
				form.activeErrors = true;

				// If Cloudflare Turnstile is enabled, reset Turnstile.
				if (typeof turnstile !== 'undefined') turnstile.reset();

				form.processing.set(false);
				return;
			})
			.catch(error => {
				const errorMessage = error.message || error.msg;

				// If there is malformed data or a non-standard error occurs, let the user know.
				let title = form.element.dataset.i18nRemoteErrorTitle;
				let content = form.element.dataset.i18nRemoteErrorContent.replace('%s', errorMessage);

				if (error.response && error.response.data) {
					title = error.response.data.title || '';
					content = error.response.data.message || error.response.data.msg || '';
				}

				SunergoPlatform.feedbackBanner.show({
					id: `${form.name}_form-error`,
					type: 'error',
					title,
					content
				});
				if (error.debug || error.res) {
					console.error(
						`[Form] ${error.debug || 'Remote Response Error - '}`,
						error.res ? error.res : error
					);
				}
				form.activeErrors = true;

				// If Cloudflare Turnstile is enabled, reset Turnstile.
				if (typeof turnstile !== 'undefined') turnstile.reset();

				form.processing.set(false);
			});
		return;
	};

	return;
};

export default initRemoteFormSubmit;